import { useContext } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";

import {
  MOTION_VAR_INITIAL,
  MOTION_VAR_ANIMATE,
  LIST_ANIMATE_VARIANTS,
} from "@constants/Animation.constants.js";
import { AuthContext } from "@contexts/AuthContext";
import useIsTablet from "@hooks/useIsTablet.js";
import useSearch from "@hooks/useSearch.js";
import useModelData from "@hooks/useModelData";
import Spinner from "@common-components/Spinner/Spinner";
import ErrorMessage from "@common-components/ErrorMessage/ErrorMessage";
import VersionPop from "@components/VersionPop/VersionPop";

function ModelList({
  onModelSelect,
  selectedTab,
  selectedProject,
  searchKey = null,
  excludedViewRef,
}) {
  const { currentUserRole } = useContext(AuthContext);
  const isTablet = useIsTablet();

  const { selectedVersions, activeList, error, isLoading } = useModelData(
    currentUserRole,
    selectedProject,
    selectedTab,
  );

  const filteredActiveList = useSearch(activeList, searchKey, [
    "code",
    "name",
    "group",
  ]);

  return (
    <Box
      ref={excludedViewRef}
      sx={{
        width: "100%",
        height: "80%",
        padding: "40px 19px 20px 55px",
        backgroundColor: "background.third",
        borderRadius: "10px",
        boxShadow: "0px 0px 10px 2px rgba(0, 0, 0, 0.3)",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          paddingRight: "20px",
          overflowY: "auto",
          scrollbarColor: "grey transparent",
          backgroundClip: "border-box",
        }}
      >
        {isLoading.modelMenuData && isLoading.versionData ? (
          <Spinner />
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : activeList.length === 0 ? (
          <ErrorMessage>No {selectedTab} model available</ErrorMessage>
        ) : (
          <motion.div
            key={selectedTab}
            initial={MOTION_VAR_INITIAL}
            animate={MOTION_VAR_ANIMATE}
            variants={LIST_ANIMATE_VARIANTS.variants}
            transition={LIST_ANIMATE_VARIANTS.transition}
          >
            <TableContainer
              component={Paper}
              sx={{ boxShadow: "none", border: "none" }}
            >
              <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead>
                  <TableRow>
                    {["Model", "Name", "Type", ""].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          color: "secondary.main",
                          fontWeight: "bold",
                          textAlign: "center",
                          padding: "10px",
                          fontSize: "16px",
                          backgroundColor: "background.third",
                          borderBottomWidth: 2,
                          borderBottomStyle: "solid",
                          borderBottomColor: "secondary.main",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredActiveList
                    .sort((a, b) =>
                      (a.code ?? "").localeCompare(b.code ?? "", undefined, {
                        sensitivity: "base",
                      }),
                    )
                    .map((model, index) => (
                      <TableRow
                        key={model.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0
                              ? "background.secondary"
                              : "background.third",
                        }}
                      >
                        <TableCell
                          sx={{
                            color: "secondary.main",
                            textAlign: "center",
                            padding: "10px",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          {model.code}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "secondary.main",
                            textAlign: "center",
                            padding: "10px",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          {model.name}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "secondary.main",
                            textAlign: "center",
                            padding: "10px",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          {model.group}
                        </TableCell>

                        <TableCell
                          sx={{ textAlign: "center", padding: "10px" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              variant="contained"
                              sx={{
                                backgroundColor: "white",
                                color: "secondary.main",
                                fontWeight: "bold",
                                width: "170px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginLeft: "auto",
                                marginRight:
                                  currentUserRole === "SALES" ||
                                  currentUserRole?.startsWith("SALES_") ||
                                  currentUserRole === "SITE" ||
                                  currentUserRole?.startsWith("SITE_") ||
                                  currentUserRole === "MANAGE" ||
                                  currentUserRole?.startsWith("MANAGE_")
                                    ? "auto"
                                    : "",
                                gap: "10px",
                                "&:hover": {
                                  backgroundColor: "white",
                                  color: "primary.btn_hover",
                                },
                              }}
                              onClick={() => {
                                const selectedVersion =
                                  selectedTab === "SALES"
                                    ? selectedVersions.sales_model_version[
                                        model.id
                                      ]
                                    : selectedTab === "COORDINATION"
                                      ? selectedVersions
                                          .coordination_model_version[model.id]
                                      : selectedTab === "IFC"
                                        ? selectedVersions.ifc_model_version[
                                            model.id
                                          ]
                                        : selectedTab === "HVAC"
                                          ? selectedVersions.hvac_model_version[
                                              model.id
                                            ]
                                          : selectedTab === "DESIGN"
                                            ? selectedVersions
                                                .design_model_version[model.id]
                                            : "";
                                if (selectedVersion) {
                                  onModelSelect({
                                    ...model,
                                    selectedVersion: selectedVersion,
                                  });
                                } else {
                                  alert("Please select a version first.");
                                }
                              }}
                            >
                              {isTablet ? "Select" : "Select Options"}
                            </Button>

                            {(currentUserRole === "ADMIN" ||
                              currentUserRole?.startsWith("ADMIN_") ||
                              currentUserRole === "MANAGE" ||
                              currentUserRole?.startsWith("MANAGE_")) && (
                              <VersionPop
                                onModelSelect={onModelSelect}
                                model={model}
                                selectedTab={selectedTab}
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        )}
      </Box>
    </Box>
  );
}

export default ModelList;
